import { apiRequest } from '@/lib/api-client';

export interface AnalyticsSummary {
  windowDays: number;
  snapshotCount: number;
  totals: {
    reach: number;
    impressions: number;
    clicks: number;
    engagements: number;
    conversions: number;
    sales: number;
    spend: number;
  };
  kpis: { ctr: number; engagementRate: number; conversionRate: number; roas: number };
}

export interface ProductPerformance {
  productId: string;
  name: string;
  sales: number;
  spend: number;
  conversions: number;
  roas: number;
}

export type RecommendationStatus = 'open' | 'accepted' | 'dismissed' | 'actioned';

export interface Recommendation {
  id: string;
  title: string;
  body: string;
  category: string;
  targetAgent: 'content' | 'media_buyer' | null;
  priority: number;
  status: RecommendationStatus;
  source: 'llm' | 'heuristic';
  createdAt: string;
}

export interface MetricInput {
  reach?: number;
  impressions?: number;
  clicks?: number;
  engagements?: number;
  conversions?: number;
  sales?: number;
  spend?: number;
}

export interface MetaCampaignInsight {
  campaignId: string;
  campaignName: string;
  reach: number;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  sales: number;
  roas: number;
  syncedAt: string;
}

export interface AnalystBriefing {
  thinking: string;
  nextSteps: string[];
  source: 'llm' | 'heuristic';
  createdAt: string;
}

const base = (orgId: string, projectId: string) =>
  `/api/v1/organizations/${orgId}/projects/${projectId}/analytics`;

export const analyticsApi = {
  summary: (orgId: string, projectId: string) =>
    apiRequest<{ summary: AnalyticsSummary; products: ProductPerformance[] }>(
      `${base(orgId, projectId)}/summary`,
    ),
  ingest: (orgId: string, projectId: string, body: MetricInput) =>
    apiRequest<{ ingested: boolean }>(`${base(orgId, projectId)}/metrics`, {
      method: 'POST',
      body: { ...body, source: 'manual' },
    }),
  listRecommendations: (orgId: string, projectId: string) =>
    apiRequest<Recommendation[]>(`${base(orgId, projectId)}/recommendations`),
  analyze: (orgId: string, projectId: string) =>
    apiRequest<{ taskId: string }>(`${base(orgId, projectId)}/analyze`, { method: 'POST' }),
  briefing: (orgId: string, projectId: string) =>
    apiRequest<AnalystBriefing | null>(`${base(orgId, projectId)}/briefing`),
  metaCampaigns: (orgId: string, projectId: string) =>
    apiRequest<MetaCampaignInsight[]>(`${base(orgId, projectId)}/campaigns`),
  syncMeta: (orgId: string, projectId: string) =>
    apiRequest<{ taskId: string }>(`${base(orgId, projectId)}/sync/meta`, { method: 'POST' }),
  getMetaSyncSchedule: (orgId: string, projectId: string) =>
    apiRequest<{ enabled: boolean; cadenceHours: number }>(
      `${base(orgId, projectId)}/sync/schedule`,
    ),
  setMetaSyncSchedule: (orgId: string, projectId: string, enabled: boolean, cadenceHours = 24) =>
    apiRequest<{ enabled: boolean; cadenceHours: number }>(
      `${base(orgId, projectId)}/sync/schedule`,
      { method: 'POST', body: { enabled, cadenceHours } },
    ),
  activate: (orgId: string, projectId: string, cadenceHours: number) =>
    apiRequest<{ scheduleId: string }>(`${base(orgId, projectId)}/schedule`, {
      method: 'POST',
      body: { cadenceHours },
    }),
  updateRec: (orgId: string, projectId: string, id: string, status: RecommendationStatus) =>
    apiRequest<Recommendation>(`${base(orgId, projectId)}/recommendations/${id}`, {
      method: 'PATCH',
      body: { status },
    }),
};
